import React, { useEffect } from 'react';
import { CheckCircle, X } from 'lucide-react';

/** How long the confirmation stays up before it bows out. */
export const SAVE_CONFIRMATION_MS = 7000;

/**
 * "Yes, that saved" — said loudly enough to be believed.
 *
 * A save is the one action in this app with nothing on screen to show for it:
 * the deck looks identical afterwards, and the file lands somewhere the
 * browser will not show you. The three-second toast in the top corner was
 * technically an answer and practically a miss — it appears at the opposite
 * end of the screen from the Save button you are still looking at, and it is
 * gone before you look up.
 *
 * So this sits under the deck, in the middle, for seven seconds, and it names
 * the file and the folder rather than just claiming success. A failed save
 * gets a dialog; a successful one should not be quieter than a rumour.
 *
 * @param {object} props
 * @param {{ fileName: string, where: string, version: number|string, at: Date, key: number }} props.save
 * @param {() => void} props.onDismiss
 * @param {number} [props.autoDismissMs] - 0 keeps it up until dismissed
 */
export default function SaveConfirmation({ save, onDismiss, autoDismissMs = SAVE_CONFIRMATION_MS }) {
  // Keyed on the save itself, so a second save restarts the clock rather than
  // inheriting what was left of the first one's.
  const saveKey = save?.key;

  useEffect(() => {
    if (!saveKey || !autoDismissMs) return undefined;
    const timer = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(timer);
  }, [saveKey, autoDismissMs, onDismiss]);

  if (!save) return null;

  return (
    <div
      // polite, not assertive: this is good news, and it should not interrupt
      // a screen reader mid-sentence to deliver it.
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 z-[115] w-[min(34rem,calc(100vw-2rem))] -translate-x-1/2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 shadow-xl"
    >
      <div className="flex items-start gap-3">
        <CheckCircle size={20} className="mt-0.5 flex-shrink-0 text-emerald-600" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-emerald-950">
            Saved{save.version ? ` — version ${save.version}` : ''}
          </p>
          <p className="mt-0.5 truncate font-mono text-xs text-emerald-900" title={save.fileName}>
            {save.fileName}
          </p>
          <p className="mt-1 text-xs text-emerald-800">{save.where}</p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-lg p-1 text-emerald-700 transition-colors hover:bg-emerald-100"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
